import { PayloadAction } from "@reduxjs/toolkit";
import { CustomerListResponse } from "app/containers/Customers/types";
import { ManufacturerListResponse } from "app/containers/Manufacturer/types";
import { ManufacturerModelListResponse } from "app/containers/ManufacturerModel/types";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getDefaultHeaders } from "utils/helpers";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import { AddOrderDropDownKey } from "../types";
import { selectAddOrderDropDowns } from "./selector";
import { addOrderModalActions } from "./slice";

const fetchingDropDowns: Record<AddOrderDropDownKey, boolean> = {
  customers: false,
  manufacturers: false,
  models: false,
};

const DROPDOWN_URL: Record<
  AddOrderDropDownKey,
  (page: number, perPage: number) => string
> = {
  customers: (page, perPage) =>
    `${API_URL}/customer/list?page=${page}&perPage=${perPage}`,
  manufacturers: (page, perPage) =>
    `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`,
  models: (page, perPage) =>
    `${API_URL}/manufacturer-model?page=${page}&perPage=${perPage}`,
};

const toNames = (key: AddOrderDropDownKey, data: unknown[]): string[] => {
  if (key === "customers") {
    return (data as CustomerListResponse["data"])
      .map((item) => item.customerName)
      .filter(Boolean);
  }
  if (key === "manufacturers") {
    return (data as ManufacturerListResponse["data"])
      .map((item) => item.menufecturerName)
      .filter(Boolean);
  }
  return Array.from(
    new Set(
      (data as ManufacturerModelListResponse["data"])
        .map((item) => item.modelDesc)
        .filter(Boolean)
    )
  );
};

function* handleGetDropDownList(action: PayloadAction<AddOrderDropDownKey>) {
  const key = action.payload;
  if (fetchingDropDowns[key]) return;
  fetchingDropDowns[key] = true;

  try {
    const dropDowns = yield select(selectAddOrderDropDowns);
    const { page, perPage } = dropDowns[key];
    const withHeaders = key !== "customers";

    const response = yield call(
      request,
      DROPDOWN_URL[key](page, perPage),
      withHeaders
        ? { method: "GET", headers: getDefaultHeaders() }
        : { method: "GET" }
    );

    const data = response?.data || [];
    const items = toNames(key, data);
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { key, data: items, meta };

    yield put(
      page === 1
        ? addOrderModalActions.getDropDownListSuccess(payload)
        : addOrderModalActions.getDropDownNextPageSuccess(payload)
    );
  } catch (error) {
    const message = (error as Error).message;
    const dropDowns = yield select(selectAddOrderDropDowns);
    const errorAction =
      dropDowns[key].page === 1
        ? addOrderModalActions.getDropDownListError
        : addOrderModalActions.getDropDownNextPageError;

    yield put(errorAction({ key, message }));
  } finally {
    fetchingDropDowns[key] = false;
  }
}

function* handleOpenAddOrderModal() {
  yield put(addOrderModalActions.getDropDownList("customers"));
  yield put(addOrderModalActions.getDropDownList("manufacturers"));
  yield put(addOrderModalActions.getDropDownList("models"));
}

export function* addOrderModalSaga() {
  yield takeLatest(
    addOrderModalActions.openAddOrderModal.type,
    handleOpenAddOrderModal
  );
  yield takeEvery(
    addOrderModalActions.getDropDownList.type,
    handleGetDropDownList
  );
  yield takeEvery(
    addOrderModalActions.getDropDownNextPage.type,
    handleGetDropDownList
  );
}
